import { useState, useEffect } from 'react'
import { useAuthStore } from '../store/auth'
import Tooltip from './Tooltip'
import LoadingButton from './LoadingButton'
import ErrorMessage from './ErrorMessage'

interface CreateJobFormProps {
  onSubmit: (data: {
    content_prompt: string
    output_quantity?: number
    media_mode: string
    publish_mode?: string
    schedule_prompt?: string
    notes?: string
    on_job_finish_webhook?: string
    on_job_finish_email?: string
    on_schedule_webhook_default?: string
    on_schedule_email_default?: string
    on_proof_webhook_default?: string
    on_proof_email_default?: string
    tracer?: string
    inbox_tracer?: string
  }) => Promise<void>
}

const getStorageKey = (userId: string | null) =>
  `${userId || 'anonymous'}-daily-instagram-story-form`

export default function CreateJobForm({ onSubmit }: CreateJobFormProps) {
  const { userId } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Form fields
  const [contentPrompt, setContentPrompt] = useState('')
  const [outputQuantity, setOutputQuantity] = useState(5)
  const [mediaMode, setMediaMode] = useState('image_only')
  const [publishMode, setPublishMode] = useState('MANUAL_REVIEW')
  const [schedulePrompt, setSchedulePrompt] = useState('')
  const [notes, setNotes] = useState('')
  const [onJobFinishWebhook, setOnJobFinishWebhook] = useState('')
  const [onJobFinishEmail, setOnJobFinishEmail] = useState('')
  const [onScheduleWebhookDefault, setOnScheduleWebhookDefault] = useState('')
  const [onScheduleEmailDefault, setOnScheduleEmailDefault] = useState('')
  const [onProofWebhookDefault, setOnProofWebhookDefault] = useState('')
  const [onProofEmailDefault, setOnProofEmailDefault] = useState('')
  const [tracer, setTracer] = useState('')
  const [inboxTracer, setInboxTracer] = useState('')

  // Load saved form data
  useEffect(() => {
    const saved = localStorage.getItem(getStorageKey(userId))
    if (saved) {
      try {
        const data = JSON.parse(saved)
        setContentPrompt(data.contentPrompt || '')
        setOutputQuantity(data.outputQuantity || 5)
        setMediaMode(data.mediaMode || 'image_only')
        setPublishMode(data.publishMode || 'MANUAL_REVIEW')
        setSchedulePrompt(data.schedulePrompt || '')
        setNotes(data.notes || '')
        setOnJobFinishWebhook(data.onJobFinishWebhook || '')
        setOnJobFinishEmail(data.onJobFinishEmail || '')
        setOnScheduleWebhookDefault(data.onScheduleWebhookDefault || '')
        setOnScheduleEmailDefault(data.onScheduleEmailDefault || '')
        setOnProofWebhookDefault(data.onProofWebhookDefault || '')
        setOnProofEmailDefault(data.onProofEmailDefault || '')
        setTracer(data.tracer || '')
        setInboxTracer(data.inboxTracer || '')
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, [userId])

  // Save form data on change
  useEffect(() => {
    localStorage.setItem(
      getStorageKey(userId),
      JSON.stringify({
        contentPrompt,
        outputQuantity,
        mediaMode,
        publishMode,
        schedulePrompt,
        notes,
        onJobFinishWebhook,
        onJobFinishEmail,
        onScheduleWebhookDefault,
        onScheduleEmailDefault,
        onProofWebhookDefault,
        onProofEmailDefault,
        tracer,
        inboxTracer,
      })
    )
  }, [
    userId,
    contentPrompt,
    outputQuantity,
    mediaMode,
    publishMode,
    schedulePrompt,
    notes,
    onJobFinishWebhook,
    onJobFinishEmail,
    onScheduleWebhookDefault,
    onScheduleEmailDefault,
    onProofWebhookDefault,
    onProofEmailDefault,
    tracer,
    inboxTracer,
  ])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!contentPrompt.trim()) return

    setLoading(true)
    setError(null)

    try {
      await onSubmit({
        content_prompt: contentPrompt.trim(),
        output_quantity: outputQuantity,
        media_mode: mediaMode,
        publish_mode: publishMode,
        schedule_prompt: schedulePrompt.trim() || undefined,
        notes: notes.trim() || undefined,
        on_job_finish_webhook: onJobFinishWebhook.trim() || undefined,
        on_job_finish_email: onJobFinishEmail.trim() || undefined,
        on_schedule_webhook_default: onScheduleWebhookDefault.trim() || undefined,
        on_schedule_email_default: onScheduleEmailDefault.trim() || undefined,
        on_proof_webhook_default: onProofWebhookDefault.trim() || undefined,
        on_proof_email_default: onProofEmailDefault.trim() || undefined,
        tracer: tracer.trim() || undefined,
        inbox_tracer: inboxTracer.trim() || undefined,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create job')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Content Prompt */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <label className="block text-sm font-medium text-gray-700">
            Content Prompt *
          </label>
          <Tooltip content="Describe what kind of Instagram stories you want AI to generate. Be specific about style, theme, and message.">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </Tooltip>
        </div>
        <textarea
          value={contentPrompt}
          onChange={(e) => setContentPrompt(e.target.value)}
          rows={3}
          required
          placeholder="E.g., Create motivational fitness stories with bold text overlays, featuring workout tips and healthy lifestyle messages. Use vibrant colors and energetic imagery."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <p className="mt-1 text-xs text-gray-500">
          AI will generate story content based on this prompt.
        </p>
      </div>

      {/* Output Quantity & Media Mode */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Quantity
            </label>
            <Tooltip content="Number of stories to generate (1-50). More stories = more credits.">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </Tooltip>
          </div>
          <input
            type="number"
            value={outputQuantity}
            onChange={(e) => setOutputQuantity(parseInt(e.target.value) || 5)}
            min={1}
            max={50}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Media Mode *
            </label>
            <Tooltip content="Choose what type of media to generate. Videos cost more credits but are more engaging.">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </Tooltip>
          </div>
          <select
            value={mediaMode}
            onChange={(e) => setMediaMode(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="image_only">Image Only</option>
            <option value="video_only">Video Only</option>
            <option value="mixed_media">Mixed Media</option>
            <option value="image_before_video">Image (Convert to Video)</option>
          </select>
        </div>
      </div>

      {/* Media Mode Description */}
      <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
        {mediaMode === 'image_only' && 'Generate static images for stories.'}
        {mediaMode === 'video_only' && 'Generate video content for stories.'}
        {mediaMode === 'mixed_media' && 'Generate a mix of images and videos.'}
        {mediaMode === 'image_before_video' &&
          'Generate images first, then convert to video after approval.'}
      </div>

      {/* Advanced Settings */}
      <details
        open={showAdvanced}
        onToggle={(e) => setShowAdvanced((e.target as HTMLDetailsElement).open)}
      >
        <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
          Advanced Settings
        </summary>

        <div className="mt-4 space-y-4 pl-4 border-l-2 border-gray-200">
          {/* Notes */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Notes
              </label>
              <Tooltip content="Personal notes about this job for your reference. Not used by AI.">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Tooltip>
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="E.g., For the summer campaign, need approval by Friday"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Publish Mode */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Publish Mode
              </label>
              <Tooltip content="Control how generated content is approved before scheduling.">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Tooltip>
            </div>
            <select
              value={publishMode}
              onChange={(e) => setPublishMode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="MANUAL_REVIEW">Manual Review</option>
              <option value="AI_REVIEW">AI Review (Auto-approve high quality)</option>
              <option value="AUTO_APPROVED">Auto-Approved (No review)</option>
            </select>
          </div>

          {/* Schedule Prompt */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Schedule Prompt
              </label>
              <Tooltip content="Tell AI when to schedule approved content. Leave empty for manual scheduling.">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Tooltip>
            </div>
            <textarea
              value={schedulePrompt}
              onChange={(e) => setSchedulePrompt(e.target.value)}
              rows={2}
              placeholder="E.g., Post daily at 9am and 7pm EST starting tomorrow"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Notifications */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Job Finish Email
                </label>
                <Tooltip content="Get notified when all content is generated.">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </div>
              <input
                type="email"
                value={onJobFinishEmail}
                onChange={(e) => setOnJobFinishEmail(e.target.value)}
                placeholder="notify@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Job Finish Webhook
                </label>
                <Tooltip content="URL to call when job completes. Receives POST with job data.">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </div>
              <input
                type="url"
                value={onJobFinishWebhook}
                onChange={(e) => setOnJobFinishWebhook(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Default Schedule Email
                </label>
                <Tooltip content="Default email for scheduled post notifications.">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </div>
              <input
                type="email"
                value={onScheduleEmailDefault}
                onChange={(e) => setOnScheduleEmailDefault(e.target.value)}
                placeholder="volunteer@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Default Schedule Webhook
                </label>
                <Tooltip content="Default webhook for scheduled post notifications.">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </div>
              <input
                type="url"
                value={onScheduleWebhookDefault}
                onChange={(e) => setOnScheduleWebhookDefault(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Default Proof Email
                </label>
                <Tooltip content="Default email to notify when a volunteer submits proof of posting.">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </div>
              <input
                type="email"
                value={onProofEmailDefault}
                onChange={(e) => setOnProofEmailDefault(e.target.value)}
                placeholder="proof-notify@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Default Proof Webhook
                </label>
                <Tooltip content="Default webhook URL called when proof is submitted. Receives POST with proof data.">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </div>
              <input
                type="url"
                value={onProofWebhookDefault}
                onChange={(e) => setOnProofWebhookDefault(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Tracers */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Tracer
                </label>
                <Tooltip content="Tag for grouping jobs. E.g., campaign-summer-2024">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </div>
              <input
                type="text"
                value={tracer}
                onChange={(e) => setTracer(e.target.value)}
                placeholder="campaign-123"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Inbox Tracer
                </label>
                <Tooltip content="Groups posts for batch volunteer processing. Volunteers see all posts with same tracer.">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </div>
              <input
                type="text"
                value={inboxTracer}
                onChange={(e) => setInboxTracer(e.target.value)}
                placeholder="volunteer-batch-1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>
      </details>

      {error && (
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
      )}

      <LoadingButton
        type="submit"
        loading={loading}
        loadingText="Creating Job..."
        disabled={!contentPrompt.trim()}
        variant="primary"
        className="w-full py-3"
      >
        Create Job
      </LoadingButton>
    </form>
  )
}
